import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { Order } from '@/@types/responses/order/Order'
import { OrderDetails } from '@/@types/responses/order/OrderDetails'
import { UpdateOrderMessage } from '@/@types/messages/UpdateOrderMessage'
import { NoteMessage } from '@/@types/messages/NoteMessage'
import { OrderPaymentMessage } from '@/@types/messages/OrderPaymentMessage'
import { MiscellaneousService } from './miscellaneous.service'
import { CreateOrderPartMessage } from '@/@types/messages/CreateOrderPartMessage'
import { CreateOrderServiceMessage } from '@/@types/messages/CreateOrderServiceMessage'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'
import { ObjectsCreatedResponse } from '@/@types/responses/common/ObjectsCreatedResponse'

export class OrderService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createOrderInvoice(orderId: number): Promise<ObjectCreatedResponse<string>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}/invoices`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<string>(json.id)
    })
  }

  public createOrderNote(orderId: number, noteMessage: NoteMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(noteMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}/notes`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createOrderPart(orderId: number, createOrderPartMessage: CreateOrderPartMessage): Promise<ObjectsCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createOrderPartMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}/order-parts`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectsCreatedResponse<number>(json.ids)
    })
  }

  public createOrderPayment(orderId: number, orderPaymentMessage: OrderPaymentMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderPaymentMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}/order-payments`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createOrderService(orderId: number, createOrderServiceMessage: CreateOrderServiceMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createOrderServiceMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}/order-services`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createQuoteFromOrder(orderId: number): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteOrder(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getOrder(id: number): Promise<OrderDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new OrderDetails(json.id, json.quickbooksInvoiceUrl, json.scheduledDate, json.approvedDate, json.approvedByUser, json.customerSuppliedParts, json.cartridgeId, json.customerId, json.customer, json.buildTypeId, json.total, json.notes, json.orderBuildSteps, json.orderParts, json.orderPayments, json.orderServices, json.onHold, json.partsDiscountPercentage, json.partsTotal, json.servicesDiscountPercentage, json.servicesTotal, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public searchOrders(
    searchTerm: string,
    pageNumber: number,
    pageSize: number,
    sortField: string,
    buildTypeId: number|undefined,
    cartridgeId: number|undefined,
    customerId: number|undefined,
    ascending: boolean,
    onHold: boolean,
    isScheduled: boolean,
    showCompletedOrders: boolean): Promise<PagingResponse<Order>> {
    let queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    if (buildTypeId)
      queryString += `&buildTypeId=${buildTypeId}`

    if (cartridgeId)
      queryString += `&cartridgeId=${cartridgeId}`

    if (customerId)
      queryString += `&customerId=${customerId}`

    if (onHold)
      queryString += '&onHold=true'

    if (isScheduled)
      queryString += '&isScheduled=true'

    if (showCompletedOrders)
      queryString += '&showCompletedOrders=true'

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<Order>
    })
  }

  public updateOrder(orderId: number, updateOrderMessage: UpdateOrderMessage): Promise<void> {
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateOrderMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public updateOrderInvoice(orderId: number): Promise<void> {
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/orders/${orderId}/invoices`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
